import { Injectable, inject } from '@angular/core';
import {
  CheckoutOutcome,
  CheckoutPlan,
  CheckoutProvider,
  CheckoutService,
  PlanPrice,
} from './checkout.service';
import { IapCheckoutService } from './iap-checkout.service';

/**
 * Choisit le moyen de paiement à l'exécution.
 *
 * Dans l'app native, c'est l'achat intégré ; partout ailleurs, Stripe. Le
 * critère est la présence du canal natif, pas une variable de compilation :
 * la même version de l'app web sert le navigateur et la WebView, et l'ancien
 * binaire des magasins — qui ne connaît pas encore ce canal — retombe
 * naturellement sur Stripe.
 *
 * C'est aussi ce qui permet de basculer sans release : le jour où le binaire
 * paraît, l'app web bascule d'elle-même.
 */
@Injectable({ providedIn: 'root' })
export class CheckoutProviderService implements CheckoutProvider {
  private readonly stripe = inject(CheckoutService);
  private readonly iap = inject(IapCheckoutService);

  /** Le fournisseur en vigueur ici et maintenant. */
  get current(): CheckoutProvider {
    return this.iap.isAvailable() ? this.iap : this.stripe;
  }

  /** L'achat passe-t-il par le magasin ? Dicte les libellés de l'écran. */
  get isStore(): boolean {
    return this.iap.isAvailable();
  }

  isAvailable(): boolean {
    return this.current.isAvailable();
  }

  getPrices(): Promise<PlanPrice[]> {
    return this.current.getPrices();
  }

  purchase(plan: CheckoutPlan): Promise<CheckoutOutcome> {
    return this.current.purchase(plan);
  }

  manageBilling(): Promise<void> {
    return this.current.manageBilling();
  }

  /**
   * Restaure les achats du magasin. Sans objet sur le web, où il n'y a rien à
   * restaurer : l'abonnement Stripe suit le compte, pas l'appareil.
   */
  restore(): Promise<boolean> {
    return this.isStore ? this.iap.restore() : Promise.resolve(false);
  }

  /** Un achat vient-il d'être lancé hors de l'app ? (Stripe seulement) */
  consumePurchaseInFlight(): boolean {
    return this.stripe.consumePurchaseInFlight();
  }
}
